import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { XMarkIcon } from "@heroicons/react/24/outline";

import StockTable from "../../components/stock/StockTable";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useParts from "../../data/useParts";
import LoadingWheel from "../../components/shared/LoadingWheel";
import PriceFormat from "../../utils/priceFormat";
import classNames from "../../utils/classNames";

type StatColor = "blue" | "emerald" | "indigo" | "sky" | "amber" | "rose";

type StockStatFilterKey =
  | "partsAddedToday"
  | "partsWithoutImages"
  | "partsWithoutMPN"
  | "qcIssues"
  | "partsWithoutLocations"
  | "missingParts";

const filterLabels: Record<StockStatFilterKey, string> = {
  partsAddedToday: "Parts Added Today",
  partsWithoutImages: "Parts Without Images",
  partsWithoutMPN: "Parts Without MPN",
  qcIssues: "Parts With QC Issues",
  partsWithoutLocations: "Parts Without Locations",
  missingParts: "Missing Parts",
};

const colorClasses: Record<StatColor, string> = {
  blue: "bg-blue-100 text-blue-600",
  emerald: "bg-emerald-100 text-emerald-600",
  indigo: "bg-indigo-100 text-indigo-600",
  sky: "bg-sky-100 text-sky-600",
  amber: "bg-amber-100 text-amber-600",
  rose: "bg-rose-100 text-rose-600",
};

const borderClasses: Record<StatColor, string> = {
  blue: "border border-blue-300",
  emerald: "border border-emerald-300",
  indigo: "border border-indigo-300",
  sky: "border border-sky-300",
  amber: "border border-amber-300",
  rose: "border border-rose-300",
};

const ringClasses: Record<StatColor, string> = {
  blue: "ring-2 ring-blue-500",
  emerald: "ring-2 ring-emerald-500",
  indigo: "ring-2 ring-indigo-500",
  sky: "ring-2 ring-sky-500",
  amber: "ring-2 ring-amber-500",
  rose: "ring-2 ring-rose-500",
};

function StockStat({
  value,
  label,
  color,
  onClick,
  active,
}: {
  value: string;
  label: string;
  color: StatColor;
  onClick?: () => void;
  active?: boolean;
}) {
  const clickable = Boolean(onClick);

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      className={classNames(
        "relative flex flex-col items-center justify-center rounded-lg px-3 py-3 text-center shadow-sm transition-transform",
        colorClasses[color],
        clickable
          ? `cursor-pointer hover:z-10 hover:scale-105 hover:shadow-md ${borderClasses[color]}`
          : "cursor-default",
        active ? ringClasses[color] : ""
      )}
    >
      <p className="text-xl font-semibold leading-none">{value}</p>
      <p className="mt-1 text-xs font-medium leading-tight">{label}</p>
    </button>
  );
}

const numberOrDash = (value: number | null | undefined): string =>
  value == null ? "-" : value.toString();

const daysOrNA = (value: number | null | undefined): string =>
  value == null ? "N/A" : `${value.toFixed(0)}d`;

export default function Stock() {
  const { t } = useTranslation("stock");
  const { stockStats } = useParts();
  const { setInfo } = usePageNameContext();
  const [statFilter, setStatFilter] = useState<StockStatFilterKey | null>(null);

  useEffect(() => {
    setInfo({
      name: t("Stock"),
      desc: t("View and manage all stock that hasn't yet been sold"),
    });
  }, []);

  const stats = stockStats.data?.data;

  const externalFilters = useMemo(() => {
    if (!statFilter) return undefined;

    if (statFilter === "partsAddedToday") {
      const now = DateTime.now().setZone("Europe/London");
      return {
        dateFieldType: "Created",
        startDate: now.startOf("day").toISO(),
        endDate: now.endOf("day").toISO(),
      };
    }

    return { [statFilter]: true };
  }, [statFilter]);

  const handleToggleFilter = (key: StockStatFilterKey) => {
    setStatFilter((current) => (current === key ? null : key));
  };

  const filterBanner = statFilter ? (
    <div className="my-2 flex items-center justify-between rounded-md border border-primary-200 bg-primary-50 px-3 py-2 text-sm text-primary-700">
      <span>
        {t("Filtered by")} <span className="font-semibold">{t(filterLabels[statFilter])}</span>
      </span>
      <button
        type="button"
        onClick={() => setStatFilter(null)}
        className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-primary-700 hover:bg-primary-100"
      >
        <XMarkIcon className="h-4 w-4" /> {t("Clear filter")}
      </button>
    </div>
  ) : null;

  return (
    <>
      {stockStats.isLoading ? (
        <LoadingWheel />
      ) : (
        <div className="relative z-10 mt-2 grid grid-cols-3 gap-2 px-1 pb-3 sm:grid-cols-5 lg:grid-cols-9">
          <StockStat
            value={PriceFormat(stats?.totalPartPrice)}
            label={t("Total Value Stock")}
            color="blue"
          />
          <StockStat
            value={numberOrDash(stats?.inStock)}
            label={t("Parts In Stock")}
            color="emerald"
          />
          <StockStat
            value={daysOrNA(stats?.averageStockAge)}
            label={t("Avg Days In Stock")}
            color="indigo"
          />
          <StockStat
            value={numberOrDash(stats?.partsAddedToday)}
            label={t("Parts Added Today")}
            color="sky"
            onClick={() => handleToggleFilter("partsAddedToday")}
            active={statFilter === "partsAddedToday"}
          />
          <StockStat
            value={numberOrDash(stats?.partsWithoutImages)}
            label={t("Parts Without Images")}
            color="amber"
            onClick={() => handleToggleFilter("partsWithoutImages")}
            active={statFilter === "partsWithoutImages"}
          />
          <StockStat
            value={numberOrDash(stats?.partsWithoutMPN)}
            label={t("Parts Without MPN")}
            color="amber"
            onClick={() => handleToggleFilter("partsWithoutMPN")}
            active={statFilter === "partsWithoutMPN"}
          />
          <StockStat
            value={numberOrDash(stats?.qcIssues)}
            label={t("Parts With QC Issues")}
            color="rose"
            onClick={() => handleToggleFilter("qcIssues")}
            active={statFilter === "qcIssues"}
          />
          <StockStat
            value={numberOrDash(stats?.partsWithoutLocations)}
            label={t("Parts Without Locations")}
            color="amber"
            onClick={() => handleToggleFilter("partsWithoutLocations")}
            active={statFilter === "partsWithoutLocations"}
          />
          <StockStat
            value={numberOrDash(stats?.missingParts)}
            label={t("Missing Parts")}
            color="rose"
            onClick={() => handleToggleFilter("missingParts")}
            active={statFilter === "missingParts"}
          />
        </div>
      )}

      <StockTable externalFilters={externalFilters} filterBanner={filterBanner} editableMpn />
    </>
  );
}
